import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getSpecialById, getMoviesByIds, getOtherSpecials } from "@/utils/movieData";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionTitle from "@/components/feature/SectionTitle";

export default function Special() {
  const { id } = useParams<{ id: string }>();
  const special = getSpecialById(id);

  useDocumentMeta({
    title: special ? `${special.title} - 秋霞电影精选专题` : "影视专题 - 秋霞电影",
    description: special
      ? `${special.intro.slice(0, 80)}...秋霞电影为你精心策划的影视专题合集。`
      : "秋霞电影精选影视专题合集",
    keywords: special
      ? `${special.title},影视专题,电影合集,秋霞电影`
      : "秋霞电影,影视专题,电影合集",
  });

  if (!special) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
          <i className="ri-bookmark-line"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">专题不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">该专题可能已下线，去首页发现更多精彩内容吧。</p>
        <Link to="/" className="mt-6 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  const others = getOtherSpecials(special.id, 3);

  return (
    <div className="bg-background-50">
      {/* cover */}
      <div className="relative h-[320px] w-full overflow-hidden bg-primary-950 md:h-[400px]">
        <img src={special.cover} alt={`${special.title} 专题封面`} title={`${special.title} - 秋霞电影专题`} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 md:px-6 md:pb-10">
            <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-300 [&_span]:text-background-400">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: "影视专题" },
                  { label: special.title },
                ]}
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {special.tags.map((t) => (
                <span key={t} className="rounded-md bg-accent-500 px-2.5 py-1 text-xs text-background-50">{t}</span>
              ))}
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-4xl">{special.title}</h1>
            <p className="mt-3 max-w-[760px] text-sm leading-relaxed text-background-200 md:text-base">{special.intro}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        {/* reason banner */}
        <div className="mb-10 flex items-center gap-3 rounded-lg border border-accent-200 bg-accent-50 px-5 py-4">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0">
            <i className="ri-thumb-up-line"></i>
          </span>
          <p className="text-sm text-accent-900">
            <strong className="font-semibold">推荐理由：</strong>
            {special.reason}
          </p>
        </div>

        {/* groups */}
        <div className="space-y-12">
          {special.groups.map((group) => {
            const groupMovies = getMoviesByIds(group.movieIds);
            return (
              <section key={group.name}>
                <div className="mb-5 flex items-center gap-3">
                  <span className="h-8 w-1 rounded-full bg-accent-500"></span>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground-950">{group.name}</h2>
                    <p className="mt-0.5 text-sm text-foreground-600">{group.description}</p>
                  </div>
                  <span className="ml-auto text-sm text-foreground-500">{groupMovies.length} 部</span>
                </div>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {groupMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* related specials */}
        <section className="mt-14">
          <SectionTitle
            eyebrow="更多专题"
            icon={<i className="ri-bookmark-3-line"></i>}
            title="相关专题推荐"
            subtitle="换一个主题，继续发现好电影"
            moreTo="/"
            moreLabel="返回首页"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {others.map((s) => (
              <Link
                key={s.id}
                to={`/special/${s.id}`}
                className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-400"
              >
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
                  <img
                    src={s.cover}
                    alt={`${s.title} 专题封面`}
                    title={`${s.title} - 秋霞电影专题`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 to-transparent"></div>
                  <h3 className="absolute bottom-3 left-3 right-3 font-heading text-base font-bold text-background-50">{s.title}</h3>
                </div>
                <p className="line-clamp-2 p-4 text-sm leading-relaxed text-foreground-600">{s.intro}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}